/* eslint-disable react/display-name */
import React, { ReactElement } from 'react';
import Phone from '@material-ui/icons/PhoneOutlined';
import Business from '@material-ui/icons/Business';
import Badge from 'components/Badge/Badge';
import CustomLink from 'components/CustomLink/CustomLink';
import CustomCheckbox from 'components/CustomCheckbox/CustomCheckbox';
import { translate } from 'components/Translation';
import styles from 'assets/jss/material-dashboard-pro-react/components/cellsStyle';
import { makeStyles } from '@material-ui/core';
import classNames from 'classnames';
import { BodyMBold, BodyM, BodyS } from 'components/Typography/Typography';
import { SingleDepartment } from 'utils/api/models/departments/singleDepartment';
import UserIcon from 'components/UserIcon/UserIcon';
import ConditionalRender from 'components/ConditionalRender/ConditionalRender';

// @ts-ignore
const useStyles = makeStyles(styles);

interface IPhoneNumberCellProps {
	text: string;
	bold?: boolean;
	color?: 'primary-light' | 'secondary-light' | 'brand' | 'success-light';
	onClick?: () => void;
}

export const PhoneNumberCell = React.memo(
	({ text, bold, color = 'secondary-light', onClick }: IPhoneNumberCellProps) => {
		const classes = useStyles();
		return (
			<div
				onClick={onClick ? onClick : undefined}
				className={classNames({
					[classes.phoneNumberCell]: true,
					[classes.cellClick]: !!onClick,
				})}
			>
				<Phone />
				<ConditionalRender
					show={!!bold}
					onTrue={() => (
						<BodyMBold color={color} disableMargin>
							{text}
						</BodyMBold>
					)}
					onFalse={() => (
						<BodyM color={color} disableMargin>
							{text}
						</BodyM>
					)}
				/>
			</div>
		);
	}
);

interface ITextCellProps {
	text: string | any;
	title?: string;
	bold?: boolean;
	color?: 'primary-light' | 'secondary-light' | 'brand' | 'danger' | 'success-light' | 'secondary-light';
	onClick?: () => void;
	maxWidth?: number;
}

export const TextCell = React.memo(
	({ text, bold, color = 'secondary-light', onClick, maxWidth, title }: ITextCellProps) => {
		const classes = useStyles({ maxWidth });

		return (
			<div
				className={classNames({
					[classes.textCell]: true,
					[classes.cellClick]: !!onClick,
				})}
			>
				{bold ? (
					<BodyMBold title={title} color={color} disableMargin>
						{text}
					</BodyMBold>
				) : (
					<BodyM title={title} disableMargin color={color}>
						{text}
					</BodyM>
				)}
			</div>
		);
	}
);

interface IDepartmentsCellProps {
	departments: SingleDepartment[];
	maxNumberOfBadges?: number;
	maxBadgeWidth?: number;
}

export const DepartmentsCell = React.memo(
	({ departments, maxNumberOfBadges = departments.length, maxBadgeWidth }: IDepartmentsCellProps) => {
		const _departments = departments.slice(0, maxNumberOfBadges);
		const more = departments.length - _departments.length;
		const classes = useStyles();

		return (
			<div className={classNames(classes.departmentsCell)}>
				{_departments.map((department) => (
					<Badge hexColor={department.uiColor} filled key={department.deptId} maxWidth={maxBadgeWidth}>
						{department.name}
					</Badge>
				))}
				{more > 0 && (
					<div className={classes.more}>
						<BodyMBold disableMargin>{translate('organization.managers.more', more)}</BodyMBold>
					</div>
				)}
				{departments.length <= 0 && (
					<BodyM color="secondary-light" disableMargin>
						{translate('numbers.details.notAssigned')}
					</BodyM>
				)}
			</div>
		);
	}
);

interface IAccountCell {
	text: string;
	icon?: ReactElement;
}

export const AccountCell = React.memo(({ text, icon }: IAccountCell) => {
	const classes = useStyles();

	return (
		<div className={classes.accountCell}>
			{icon ? icon : <Business />}
			<BodyM disableMargin>{text}</BodyM>
		</div>
	);
});

interface IIconCellProps {
	text: string;
	icon: ReactElement;
}

export const IconCell = React.memo(({ text, icon }: IIconCellProps) => {
	const classes = useStyles();

	return (
		<div className={classes.iconCell}>
			{text ? (
				<>
					{icon}
					<BodyM disableMargin>{text}</BodyM>
				</>
			) : (
				<BodyM disableMargin color="secondary-light">
					{translate('numbers.details.notAssigned')}
				</BodyM>
			)}
		</div>
	);
});

interface IUserNameCellProps {
	text: string;
	type?: types.users.userType;
}

export const UserNameCell = React.memo(({ text, type }: IUserNameCellProps) => {
	return <IconCell text={text} icon={<UserIcon type={type} />} />;
});

interface ILinkCellProps {
	path: string;
	text?: string;
	icon?: ReactElement;
	disabled?: boolean;
	props?: Object;
}

export const LinkCell = React.memo(({ path, text, icon, disabled = false, props }: ILinkCellProps) => {
	const classes = useStyles();
	return (
		<div
			className={classNames(
				{
					[classes.linkCell]: true,
					[classes.disabled]: disabled,
				},
				//@ts-ignore
				props?.underline ? classNames(classes.underline) : ''
			)}
		>
			{text ? (
				/** added to show aamali+services, in case of micro bundle when the route changes
					Props are optional will be passed to Link */
				<CustomLink to={path} color="brand" type="bodyMBold" props={props}>
					<>{icon}</>
					<>{text}</>
				</CustomLink>
			) : (
				<BodyM color="secondary-light" disableMargin>
					{translate('numbers.details.notAssigned')}
				</BodyM>
			)}
		</div>
	);
});

interface ICheckboxCellProps {
	text: string;
	checked: boolean;
	onClick: Function;
	labelProps?: object;
}

export const CheckboxCell = React.memo(({ text, checked, onClick, labelProps }: ICheckboxCellProps) => {
	const classes = useStyles();
	return (
		<div className={classes.checkboxCell}>
			<CustomCheckbox
				disabled={false}
				label={text}
				checked={checked}
				onClick={onClick}
				size="xs"
				labelProps={labelProps}
			/>
		</div>
	);
});

interface IHeaderCellProps {
	text: string;
}

export const HeaderCell = ({ text }: IHeaderCellProps) => {
	const classes = useStyles();
	return (
		<div className={classes.headerCell}>
			<BodyS color="secondary-light" disableMargin>
				{text}
			</BodyS>
		</div>
	);
};
